import { useInvestorAccountModel } from '@common/models/InvestorAccountModel';
import { useBankAccountsModel } from '@common/models/BankAccountsModel';
import currency from '@common/utility/currency';
import React, { useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import SplitView from 'src/apps/invest/common/shells/SplitView';
import { ROUTES } from 'src/apps/invest/config';
import { PaymentSourceSelector } from 'src/apps/invest/common/components/PaymentSourceSelector';
import { useCheckoutCashflowViewModel } from '../../models/CheckoutCashflowViewModel';
import Amount from '../components/Amount';
import { Schedule } from '../components/Amount/Schedule';
import { Header } from '../components/Header';
import { Navigation } from '../components/Navigation';
import { Section } from '../components/Section';
import Summary from '../components/Summary';
import PreflightModal from 'src/apps/invest/common/components/PreflightModal';
import { InvestorAccount, InvestPreflightHint, RegulatoryRuleType } from '@services/appsync/types';
import { CampaignSelector } from '../components/CampaignSelector';

export const CheckoutCashflowAmountPage: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [preflightHint, setPreflightHint] = useState<InvestPreflightHint | null>(null);
  const viewModel = useCheckoutCashflowViewModel();
  const form = useForm<{ amount: number }>({
    defaultValues: {
      amount: viewModel.amount,
    },
  });
  const amountWatch = useWatch({ control: form.control, name: 'amount' });
  const investorModel = useInvestorAccountModel();
  const bankAccountsModel = useBankAccountsModel();
  const bankAccount = bankAccountsModel.getDefaultBankAccount();

  const handleFormSubmission = form.handleSubmit(async () => {
    setIsLoading(true);
    const result = await viewModel.handleAmountFormSubmit();

    // Guide the user if the preflight check returned any hints.
    if (!result.success && result.preflightHints && result.preflightHints.length > 0) {
      const firstHint = result.preflightHints[0];
      setPreflightHint(firstHint ?? null);
    } else if (!result.success) {
      form.setError('amount', {
        message:
          result.error || "Something went wrong. Please try again and contact us if you're still having trouble.",
      });
    }

    setIsLoading(false);
  });

  const handleSuggestedAmount = (suggestedAmount: number) => {
    const amount = parseInt(suggestedAmount.toString());
    form.setValue('amount', amount);
    form.clearErrors('amount');
    viewModel.setAmount(amount);
  };

  const isFullyAccredited = (investorAccount: InvestorAccount): boolean => {
    return Boolean(
      investorAccount?.accreditation_manual ||
        investorAccount?.accreditation_hellosign ||
        investorAccount?.accreditation,
    );
  };

  const isRegD = investorModel.subscriptionOffer?.regulatory_rule_type === RegulatoryRuleType.REG_D_506C;

  return (
    <SplitView.UI columnRatio={60}>
      <SplitView.LeftColumn>
        {preflightHint && <PreflightModal preflightHint={preflightHint} onClose={() => setPreflightHint(null)} />}
        <Amount.Form onSubmit={handleFormSubmission}>
          <Header
            title="Invest in Cash Flow"
            breadCrumb={
              <Navigation
                links={[
                  {
                    href: ROUTES.dashboard,
                    label: 'Dashboard',
                  },
                  { isCurrent: true, label: 'Amount' },
                ]}
              />
            }
          />
          {investorModel.investorAccount && isFullyAccredited(investorModel.investorAccount) && (
            <Section title="Offering Method" description="Select your preferred investment method.">
              <CampaignSelector />
            </Section>
          )}
          <Section pb="5px" title="Schedule">
            <Schedule />
          </Section>
          <Section title="Investment Amount" description="Please enter the amount you would like to invest.">
            <Amount.AmountField form={form} />
            {investorModel.cashflowEligibility?.eligible === false && <Amount.Eligibility />}
            <Amount.AutoSuggestions form={form} onSuggestedAmount={handleSuggestedAmount} />
          </Section>
          <Section
            icon="lock"
            title="Payment Information"
            description="Please select the payment method you would like to use.">
            <PaymentSourceSelector />
          </Section>
        </Amount.Form>
      </SplitView.LeftColumn>
      <SplitView.RightColumn gap={'31px'}>
        <Summary.Container label="Purchase Summary">
          <Summary.Item
            label={isRegD ? 'Cash Flow (Accredited)' : 'Cash Flow'}
            image="cash-flow-icon"
            value={currency.format(!isNaN(amountWatch) ? amountWatch : 0)}
          />
          {bankAccount && (
            <Summary.Item
              label="Account"
              value={`${bankAccount.name} - ${bankAccount.account_type} (...${bankAccount.account_number || ''})`}
            />
          )}
        </Summary.Container>
        <Amount.Form onSubmit={handleFormSubmission}>
          <Amount.SubmitButton
            isDisabled={
              !amountWatch ||
              String(amountWatch) === '0' ||
              Boolean(form.formState.errors.amount) ||
              investorModel.cashflowEligibility?.eligible === false ||
              isLoading ||
              !bankAccount
            }
            isLoading={isLoading}
          />
        </Amount.Form>
      </SplitView.RightColumn>
    </SplitView.UI>
  );
};
